import React from 'react';
import { Link } from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { DateTime } from 'luxon';
import type { BDirectRow } from '../../../hooks/useBDirectRows';
import {
  BuildHeader,
  BuildStatusRow,
  BuildTime,
  BuildStatus,
  ClassicPipelineLabel,
  ClassicPipelineWarning,
  RegionDash,
  StatusChip,
} from './styles';

const formatBuildTime = (iso: string | undefined): string => {
  if (!iso) return '—';
  const dt = DateTime.fromISO(iso).setZone('Pacific/Auckland');
  if (!dt.isValid) return iso;
  return dt.toFormat('dd/MM HH:mm');
};

export const BuildCell: React.FC<{ row: BDirectRow; release: string }> = ({
  row,
  release,
}) => {
  if (!row.repo) return <RegionDash>—</RegionDash>;
  const build = row.build;
  if (!build) {
    return (
      <RegionDash>
        No build for this <strong>{release}</strong> release
      </RegionDash>
    );
  }
  const { status, result, webUrl, buildNumber } = build;
  let label: string;
  let chipStatus: BuildStatus;
  if (status === 'completed') {
    switch (result) {
      case 'succeeded':
      case 'partiallySucceeded':
        label = 'Succeeded';
        chipStatus = 'succeeded';
        break;
      case 'failed':
        label = 'Failed';
        chipStatus = 'failed';
        break;
      case 'canceled':
        label = 'Canceled';
        chipStatus = 'neutral';
        break;
      default:
        label = result ?? 'Completed';
        chipStatus = 'neutral';
    }
  } else if (status === 'inProgress' || status === 'cancelling') {
    label = status === 'cancelling' ? 'Cancelling' : 'In progress';
    chipStatus = 'inProgress';
  } else {
    label = status === 'notStarted' ? 'Queued' : status;
    chipStatus = 'neutral';
  }
  return (
    <BuildStatusRow>
      <BuildHeader>
        {webUrl ? (
          <Link href={webUrl} target="_blank" rel="noopener noreferrer">
            {buildNumber}
          </Link>
        ) : (
          <span>{buildNumber}</span>
        )}
        <StatusChip size="small" label={label} status={chipStatus} />
      </BuildHeader>
      <BuildTime>
        {formatBuildTime(
          build.finishTime ?? build.startTime ?? build.queueTime,
        )}
        {build.kind === 'release' && (
          <ClassicPipelineLabel>
            <ClassicPipelineWarning>
              <WarningRoundedIcon fontSize="small" />
            </ClassicPipelineWarning>
            Classic pipeline
          </ClassicPipelineLabel>
        )}
      </BuildTime>
    </BuildStatusRow>
  );
};
